import { faShare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, DialogTitle, Grid, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { FC, useContext, useMemo, useState } from "react";
import { Permissions, Report } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import { StyledButton, StyledIconButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import { StyledLink } from "../../components/utility/StyledLink";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";
import { StyledDialog, StyledDialogActions, StyledDialogContent } from "../../forms/inputs/CommonFormElements";
import { getScheduleString } from "../../utility/EnumValueGetters";
import usePostEmailManualTrigger from "../../hooks/actions/usePostEmailManualTrigger";
import usePermissions from "../../hooks/auth/usePermissions";

const useEmailOnlyReportsColumns = (onSendEmailClick: (report: Report) => void) => {
    const {data: templates} = useContext(ReportTemplatesContext);
    const {data: permissions} = usePermissions();

    const emailOnlyReportColumns = useMemo(() => {
        const columns: GridColDef<Report>[] = [
            {
                field: 'name',
                headerName: "Report Name",
                flex: 1,
                renderCell: (params) => {
                    return (
                        <StyledLink 
                            to={`/reports/edit/${params.row.id}`}
                        >
                            {params.formattedValue}
                        </StyledLink>
                    )
                }
            },
            {
                headerName: 'Template',
                field: 'templateId',
                valueGetter: (value) => {return templates?.filter(template => template.id == value )[0]?.name ?? value},
                flex: 1
            },
            {
                headerName: 'Schedule',
                field: 'scheduleType',
                valueGetter: getScheduleString,
                width: 200
            },
            {
                headerName: '',
                field: 'actions',
                renderCell: (params) => (
                    <GridActions 
                        editUrl={`/reports/edit/${params.row.id}`}
                        deleteUrl={`/reports/delete/${params.row.id}`}
                    />
                ),
                disableExport: true
            }
        ];

        if(permissions?.includes(Permissions.AdminAccess)) {
            columns.splice(0, 0, {
                field: 'sendAction',
                headerName: '',
                renderCell: (params) => {
                    return (
                        <Tooltip
                            title="Trigger Email"
                        >
                            <StyledIconButton
                                size="small"
                                onClick={() => onSendEmailClick(params.row)}
                            >
                                <FontAwesomeIcon icon={faShare} />
                            </StyledIconButton>
                        </Tooltip>
                    )
                },
                width: 50,
                align: 'left',
                disableExport: true,
            });
        }

        return columns;
    }, [templates, permissions])

    return emailOnlyReportColumns;
}

interface IEmailOnlyReportsProps {
    reports: Report[];
}

const EmailOnlyReports: FC<IEmailOnlyReportsProps> = ({
    reports
}) => {
    const theme = useTheme();
    const emailReport = usePostEmailManualTrigger();

    const [reportToEmail, setReportToEmail] = useState<Report | null>(null);
    const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState<boolean>(false);

    const onSendEmailClick = (report: Report) => {
        setSendEmailDialogOpen(true);
        setReportToEmail(report);
    };

    const onSendMailConfirm = async () => {
        if(reportToEmail) {
            await emailReport(reportToEmail);
        }
        closeDialog();
    }

    const closeDialog = () => {
        setSendEmailDialogOpen(false);
        setReportToEmail(null);
    }

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Email Only Reports`,
        exportTitle: `Email Only Reports`,
        includeDateTime: true
    }), []);

    const columns = useEmailOnlyReportsColumns(onSendEmailClick);
    return (
        <>
            <StyledDialog 
                onClose={() => setSendEmailDialogOpen(false)}
                open={sendEmailDialogOpen}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    Send Email
                </DialogTitle>
                <StyledDialogContent>
                    <Box
                        sx={{
                            mb: 2
                        }}
                    >
                        <Typography>
                            Are you sure you want to email the following report?
                        </Typography>
                    </Box>
                    <Grid
                        container
                        gap={1}
                    >
                        <Grid
                            size={{
                                xs: 12
                            }}
                            sx={{
                                display: "flex"
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 6
                                }}
                            >
                                Name:
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6
                                }}
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    textAlign: "right"
                                }}
                            >
                                {reportToEmail?.name}
                            </Grid>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12
                            }}
                            sx={{
                                display: "flex"
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 6
                                }}
                            >
                                To:
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6
                                }}
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    textAlign: "right"
                                }}
                            >
                                {reportToEmail?.mailingList}
                            </Grid>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12
                            }}
                            sx={{
                                display: "flex"
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 6
                                }}
                            >
                                CC:
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6
                                }}
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    textAlign: "right"
                                }}
                            >
                                {reportToEmail?.ccList}
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledDialogContent>
                <StyledDialogActions>
                    <StyledButton
                        onClick={async () => {await onSendMailConfirm();}}
                    >
                        Confirm
                    </StyledButton>
                    <StyledButton
                        onClick={closeDialog}
                    >
                        Cancel
                    </StyledButton>
                </StyledDialogActions>
            </StyledDialog>
            <Paper
                sx={{
                    marginBottom: 2
                }}
            >
                <CollapsibleTablePremium 
                    density="compact"
                    title={"Email Only Reports"} 
                    loading={false} 
                    columns={columns}
                    rows={reports}
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...customToolbarProps}
                />
            </Paper>
        </>
    )
}

export default EmailOnlyReports;